import {
  bindColor,
  createShapeElement,
  LINE_END_LABELS,
  LINE_ENDS,
  type LineEnd,
  lineGeometry,
  type SceneElement,
  type ShapeElement,
} from "@cva/shared";
import { cn } from "../lib/utils";
import { addElements } from "./commands";
import type { EditorStore } from "./store";
import { activePageDoc, useEditorApi } from "./store";
import { NumberField, Row, Tip, Toggle } from "./ui";

/**
 * Lines & arrows: presets for the elements panel and the cap / bend controls
 * of the properties panel. A line stays a `shape` with kind `line` (a rotated
 * box, the line runs through its middle); caps are stored as `lineStart` /
 * `lineEnd` and drawn by the renderer (scene/line.ts).
 */

export type LinePreset = "line" | "arrow" | "double" | "dashed-arrow" | "curved";

export const LINE_PRESETS: { id: LinePreset; label: string }[] = [
  { id: "line", label: "Linie" },
  { id: "arrow", label: "Pfeil" },
  { id: "double", label: "Doppelpfeil" },
  { id: "dashed-arrow", label: "Gestrichelter Pfeil" },
  { id: "curved", label: "Gebogener Pfeil" },
];

function presetFields(preset: LinePreset): Partial<ShapeElement> {
  switch (preset) {
    case "line":
      return {};
    case "arrow":
      return { lineEnd: "triangle" };
    case "double":
      return { lineStart: "triangle", lineEnd: "triangle" };
    case "dashed-arrow":
      return { lineEnd: "arrow", lineRound: true };
    case "curved":
      return { lineEnd: "arrow", lineRound: true, curve: 0.35 };
  }
}

/** Adds a line or arrow in the middle of the page, half the page wide. */
export function addLinePreset(store: EditorStore, preset: LinePreset) {
  const { brand } = store.getState();
  const doc = activePageDoc(store);
  const size = Math.round(Math.min(doc.format.width, doc.format.height) * 0.3);
  const strokeWidth = Math.max(2, Math.round(size * 0.02));
  const width = Math.round(doc.format.width * 0.5);
  const height = Math.max(strokeWidth * 3, 12);
  const element = createShapeElement("line", {
    x: (doc.format.width - width) / 2,
    y: (doc.format.height - height) / 2,
    width,
    height,
    fill: "#2457eb",
    stroke: {
      color: "#111111",
      width: strokeWidth,
      dash: preset === "dashed-arrow" ? "dashed" : "solid",
    },
    ...presetFields(preset),
  });
  if (brand) bindColor(element, "stroke", "text", brand);
  addElements(store, [element]);
}

/** Small preview of a line preset or cap (tiles and pickers). */
export function LinePreview({
  start,
  end,
  dash,
  curve,
  className,
}: {
  start?: LineEnd | undefined;
  end?: LineEnd | undefined;
  dash?: boolean;
  curve?: number | undefined;
  className?: string;
}) {
  const width = 40;
  const height = 24;
  const strokeWidth = 2.5;
  const geometry = lineGeometry({ width, height, strokeWidth, start, end, curve });
  return (
    <svg
      viewBox={`-4 0 ${width + 8} ${height}`}
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
    >
      <path
        d={geometry.d}
        strokeWidth={strokeWidth}
        strokeDasharray={dash ? "6 4" : undefined}
        strokeLinecap="round"
      />
      {geometry.caps.map((cap, index) => (
        <path
          // biome-ignore lint/suspicious/noArrayIndexKey: caps are positional
          key={index}
          d={cap.d}
          fill={cap.filled ? "currentColor" : "none"}
          stroke={cap.filled ? "none" : "currentColor"}
          strokeWidth={strokeWidth}
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

export function linePresetPreview(preset: LinePreset) {
  const fields = presetFields(preset);
  return (
    <LinePreview
      start={fields.lineStart}
      end={fields.lineEnd}
      curve={fields.curve ? fields.curve * 0.7 : undefined}
      dash={preset === "dashed-arrow"}
      className="!h-6 !w-10"
    />
  );
}

function CapPicker({
  label,
  value,
  mirrored,
  onChange,
}: {
  label: string;
  value: LineEnd | undefined;
  mirrored?: boolean;
  onChange: (value: LineEnd) => void;
}) {
  return (
    <div>
      <p className="mb-1 text-xs text-zinc-500">{label}</p>
      <div className="grid grid-cols-6 gap-1">
        {LINE_ENDS.map((kind) => {
          const active = (value ?? "none") === kind;
          return (
            <Tip key={kind} label={LINE_END_LABELS[kind]}>
              <button
                type="button"
                aria-label={`${label}: ${LINE_END_LABELS[kind]}`}
                aria-pressed={active}
                onClick={() => onChange(kind)}
                className={cn(
                  "flex h-8 items-center justify-center rounded-md border text-zinc-600",
                  active
                    ? "border-brand-500 bg-brand-50 text-brand-700"
                    : "border-zinc-200 hover:bg-zinc-50",
                )}
              >
                <LinePreview end={kind} className={cn("h-4 w-7", mirrored && "-scale-x-100")} />
              </button>
            </Tip>
          );
        })}
      </div>
    </div>
  );
}

/** Caps, round ends and bend of selected lines. */
export function LineCapFields({ elements }: { elements: ShapeElement[] }) {
  const store = useEditorApi();
  const first = elements[0]!;
  const ids = elements.map((element) => element.id);
  const update = (recipe: (target: Extract<SceneElement, { type: "shape" }>) => void) =>
    store.getState().update((draft) => {
      for (const id of ids) {
        const target = draft.elements[id];
        if (target?.type === "shape" && target.shape === "line") recipe(target);
      }
    });
  const setCap = (key: "lineStart" | "lineEnd", value: LineEnd) =>
    update((target) => {
      if (value === "none") delete target[key];
      else target[key] = value;
    });
  return (
    <div className="space-y-2">
      <CapPicker
        label="Anfang"
        mirrored
        value={first.lineStart}
        onChange={(value) => setCap("lineStart", value)}
      />
      <CapPicker
        label="Ende"
        value={first.lineEnd}
        onChange={(value) => setCap("lineEnd", value)}
      />
      <Row>
        <button
          type="button"
          onClick={() =>
            update((target) => {
              const start = target.lineStart;
              const end = target.lineEnd;
              if (end) target.lineStart = end;
              else delete target.lineStart;
              if (start) target.lineEnd = start;
              else delete target.lineEnd;
            })
          }
          className="inline-flex h-8 flex-1 items-center justify-center rounded-md border border-zinc-200 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
        >
          Richtung umkehren
        </button>
      </Row>
      <Toggle
        checked={Boolean(first.lineRound)}
        onChange={(checked) =>
          update((target) => {
            if (checked) target.lineRound = true;
            else delete target.lineRound;
          })
        }
        label="Runde Enden"
      />
      <NumberField
        label="Biegung"
        value={Math.round((first.curve ?? 0) * 100)}
        unit="%"
        min={-100}
        max={100}
        onChange={(v) => {
          const curve = Math.max(-1, Math.min(1, v / 100));
          update((target) => {
            if (Math.abs(curve) < 0.005) delete target.curve;
            else target.curve = curve;
          });
        }}
      />
    </div>
  );
}
